import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { FlatpickrDirective, provideFlatpickrDefaults } from 'angularx-flatpickr';
import { getFlatpickrDefaults } from '../flatpickr-defaults';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-form-modal',
  imports: [CommonModule, FormsModule, FlatpickrDirective],
  templateUrl: './form-modal.component.html',
  styleUrl: './form-modal.component.scss',
    providers: [
      provideFlatpickrDefaults(getFlatpickrDefaults())
    ],
})
export class FormModalComponent implements OnChanges {

  @Input() isOpen = false;
  @Input() position: 'left' | 'bottom' = 'left';
  @Input() eyebrow?: string;
  @Output() closed = new EventEmitter<void>();

  today: Date = new Date();

  getMinDate(item: any): any {
    if (!item) return undefined;
    const min = item.minDate ?? item.min_date;
    if (min === undefined || min === null) return undefined;
    if (typeof min === 'string') {
      const trimmed = min.trim();
      if (!trimmed || trimmed.toLowerCase() === 'none' || trimmed.toLowerCase() === 'unlimited' || trimmed.toLowerCase() === 'null') return undefined;
      if (trimmed.toLowerCase() === 'today') return 'today';
      return trimmed;
    }
    return min;
  }

  getMaxDate(item: any): any {
    if (!item) return undefined;
    const max = item.maxDate ?? item.max_date;
    if (max === undefined || max === null) return undefined;
    if (typeof max === 'string') {
      const trimmed = max.trim();
      if (!trimmed || trimmed.toLowerCase() === 'none' || trimmed.toLowerCase() === 'unlimited' || trimmed.toLowerCase() === 'null') return undefined;
      if (trimmed.toLowerCase() === 'today') return 'today';
      return trimmed;
    }
    return max;
  }

  close(): void {
    this.isOpen = false;
    this.closed.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    this.close();
  }

  colClass(items: any): string {
    const requested = Number(items.col) || 12;
    const clamped = Math.min(12, Math.max(1, requested));
    return 'col-' + clamped;
  }

  @Input() apiKey: string = ''
  @Input() header: string = ''
  @Input() modalId: string = ''
  @Input() Modaltitle: string = ''
  @Input() leftButtonTitle: string = ''
  @Input() rightButtonTitle: string = ''
  @Output() value = new EventEmitter<string>()
  @Input() inputValue: string = '';
  @Input() Singlefields: boolean = false;
  @Input() Multiplefields: boolean = false;
  @Input() FormArray: any[] = [];
  @Output() fomrresponse = new EventEmitter<any>();
  @Input() singleFieldType: string = 'text';
  @Input() conversionType: string = 'decimal';
  @Input() bitCount: number = 12;
  @Input() bitLabels: string[] = [];
  @Input() bitLabelsStr: string = '';
  @Input() minBase?: number | string;
  @Input() maxBase?: number | string;
  @Input() baseValue?: number | string;
  @Input() bitDirection: 'ltr' | 'rtl' = 'ltr';
  @Input() hideCheckboxes: boolean = false;
  @Input() rowData: any = {};
  singleFieldBits: any[] = [];
  displayedSingleFieldBits: any[] = [];
  singleFieldBaseValue: bigint = 0n;
  singleFieldCalculatedTotal: string = '0';
  singleFieldHasMinBase: boolean = false;
  singleFieldMinBaseBig: bigint = 0n;
  siteId: string = '';
  submitted = false;

  constructor(
    private router: Router,
    private http: HttpClient,
    private toast: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['FormArray'] && this.FormArray?.length) {
      this.sortFormArrayByOrder();
      if (!this.siteId) this.getsiteid();
      if (this.FormArray !== null) {
        this.initializeDateFields();
        this.initializeBitFields();
        this.normalizeDropdownValues();
        this.loadDropdownOptions();
        this.evaluateAllFieldConditions();
      }
    }
    if (changes['rowData']) {
      this.evaluateAllFieldConditions();
    }
    if (this.Singlefields && this.singleFieldType === 'bitcase') {
      this.initSingleFieldBits();
    }
  }

  private sortFormArrayByOrder(): void {
    if (!Array.isArray(this.FormArray) || this.FormArray.length <= 1) return;
    const hasOrder = this.FormArray.some(f => {
      const raw = f?.Order_By ?? f?.order_by ?? f?.orderBy;
      return raw !== undefined && raw !== null && raw !== '' && !isNaN(Number(raw));
    });
    if (!hasOrder) return;
    this.FormArray.sort((a, b) => {
      const rawA = a?.Order_By ?? a?.order_by ?? a?.orderBy;
      const rawB = b?.Order_By ?? b?.order_by ?? b?.orderBy;
      const numA = (rawA !== undefined && rawA !== null && rawA !== '' && !isNaN(Number(rawA))) ? Number(rawA) : Number.MAX_SAFE_INTEGER;
      const numB = (rawB !== undefined && rawB !== null && rawB !== '' && !isNaN(Number(rawB))) ? Number(rawB) : Number.MAX_SAFE_INTEGER;
      return numA - numB;
    });
  }

  private initializeDateFields(): void {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    this.FormArray.forEach(field => {
      if (field?.type === 'date' && field?.readonly) {
        const todayStr = field.monthYear ? `${year}-${month}` : `${year}-${month}-${day}`;
        field.value = todayStr;
      }
    });
  }

  private getsiteid() {
    const urlTree = this.router.parseUrl(this.router.url);
    const encodedId = urlTree.queryParams['siteid'] || localStorage.getItem('site_idLogin');
    let decodedSiteId = '';
    if (encodedId) {
      try {
        decodedSiteId = atob(encodedId);
      } catch (e) {
        decodedSiteId = '';
      }
    }
    this.siteId = decodedSiteId;
  }

  validateInput(item: any) {
    if (!item) return;
    item.isDirty = true;
    const val = item.value !== undefined && item.value !== null ? String(item.value).trim() : '';

    // If required and empty
    if (item.required && !item.readonly && !item.skipRequiredValidation && !val) {
      item.isValid = false;
      item.isRequiredMissing = true;
      return;
    }
    item.isRequiredMissing = false;

    if (!item.regex) {
      item.isValid = true;
      return;
    }
    try {
      const pattern = new RegExp(item.regex);
      item.isValid = pattern.test(val);
    } catch (e) {
      item.isValid = false;
    }
    this.evaluateAllFieldConditions();
  }

  compareSelectOptions = (a: any, b: any): boolean => {
    if (a === b) return true;
    if (a === null || a === undefined || a === '' || b === null || b === undefined || b === '') return false;
    return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  };

  private loadDropdownOptions(): void {
    this.FormArray.forEach(field => {
      if (field.dropdown && field.apiUrl) {
        if (Array.isArray(field.options) && field.options.length > 0) {
          this.normalizeDropdownValue(field);
          return;
        }
        let params = new HttpParams().set('site_id', this.siteId);
        if (field.apiUrl === 'Maintenance-Location-List') {
          params = params.set('type', 'maintenance');
        }
        if (field.payload && typeof field.payload === 'object') {
          Object.entries(field.payload).forEach(([k, v]) => {
            if (v !== undefined && v !== null) params = params.set(k, String(v));
          });
        }
        this.http.get<any>(`${environment.apiURL}/${field.apiUrl}`, { params }).subscribe({
          next: (data: any) => {
            const list = data?.Data?.list ?? data?.Data ?? [];
            const idKey = field.option_mapping?.id ?? field.optionKey ?? 'id';
            const nameKey = field.option_mapping?.name ?? field.id ?? 'name';
            let options = (Array.isArray(list) ? list : []).map((item: any) => ({
              id: item[idKey] ?? item.id,
              name: item[nameKey] ?? item.name ?? item[idKey] ?? item.id,
              ...item
            }));

            const extra = field.extraOption || field.allOption;
            let extraItem: any = null;
            if (extra && (extra.id !== undefined || extra.name !== undefined)) {
              extraItem = { id: extra.id ?? 'ALL', name: extra.name ?? 'All Units' };
            } else if (field.mapping?.json) {
              const jsonMap = typeof field.mapping.json === 'string' ? JSON.parse(field.mapping.json) : field.mapping.json;
              if (jsonMap && typeof jsonMap === 'object') {
                const entries = Object.entries(jsonMap);
                if (entries.length) extraItem = { id: entries[0][0], name: String(entries[0][1]) };
              }
            } else if (field.options?.json) {
              const jsonMap = typeof field.options.json === 'string' ? JSON.parse(field.options.json) : field.options.json;
              if (jsonMap && typeof jsonMap === 'object') {
                const entries = Object.entries(jsonMap);
                if (entries.length) extraItem = { id: entries[0][0], name: String(entries[0][1]) };
              }
            } else if (field.includeAll) {
              extraItem = { id: 'ALL', name: 'All Units' };
            }

            if (extraItem) {
              options = [
                extraItem,
                ...options.filter((opt: any) => String(opt.id) !== String(extraItem.id))
              ];
            }

            field.options = options;
            this.normalizeDropdownValue(field);
          },
          error: (err) => {
            console.error(`Failed to load options for field "${field.id}":`, err);
            field.options = [];
          }
        });
      }
    });
  }

  private normalizeDropdownValues(): void {
    this.FormArray.forEach(field => this.normalizeDropdownValue(field));
  }

  /**
   * Edit rows can contain UI placeholders such as `N/A` instead of a real
   * dropdown value. Treat those values exactly like an unselected field so
   * the select control displays its normal "Select …" placeholder.
   * Matches across ID, Name, matchKey, or optionKey with loose type equality.
   */
  private normalizeDropdownValue(field: any): void {
    if (!field?.dropdown) return;

    if (this.isPlaceholderValue(field.value)) {
      field.value = '';
      return;
    }

    if (!field.options?.length) return;

    this.matchAndSetDropdownValue(field);
  }

  private fetchDropdownOptions(field: any): void {
    const url = `${environment.apiURL}/${field.apiUrl}`;
    const cleanPayload = this.buildCleanPayload(field.payload);

    this.http.post<any>(url, cleanPayload).subscribe({
      next: (res) => {
        field.options = Array.isArray(res?.Data?.list)
          ? res.Data.list
          : Array.isArray(res?.Data)
            ? res.Data
            : Array.isArray(res)
              ? res
              : [];

        if (field.extraOption && typeof field.extraOption === 'object') {
          const extraOpt = {
            id: field.extraOption.id || field.extraOption.value || 'ALL',
            name: field.extraOption.name || field.extraOption.label || 'All'
          };
          const alreadyExists = field.options.some((o: any) => o.id === extraOpt.id);
          if (!alreadyExists) {
            field.options.unshift(extraOpt);
          }
        }

        if (field.value !== undefined && field.value !== null && field.value !== '') {
          this.matchAndSetDropdownValue(field);
        }
      },
      error: (err) => {
        console.error(`Failed to load options for ${field.id}:`, err);
        field.options = [];
      }
    });
  }

  private buildCleanPayload(rawPayload: any): any {
    if (!rawPayload || typeof rawPayload !== 'object') {
      return this.siteId ? { site_id: this.siteId } : {};
    }

    const payload: any = { ...rawPayload };
    if (!payload.site_id && this.siteId) {
      payload.site_id = this.siteId;
    }
    return payload;
  }

  private matchAndSetDropdownValue(field: any): void {
    if (!field.options || !field.options.length) return;

    const strVal = String(field.value).trim().toLowerCase();

    // 1. Direct ID match (handles number vs string equality)
    let match = field.options.find((opt: any) =>
      opt.id !== undefined && String(opt.id).trim().toLowerCase() === strVal
    );

    // 2. Match by Name / Label
    if (!match) {
      match = field.options.find((opt: any) =>
        opt.name !== undefined && String(opt.name).trim().toLowerCase() === strVal
      );
    }

    // 3. Match by matchKey if configured on the field
    if (!match && field.matchKey) {
      match = field.options.find((opt: any) =>
        opt[field.matchKey] !== undefined && String(opt[field.matchKey]).trim().toLowerCase() === strVal
      );
    }

    // 4. Match by optionKey on the item
    if (!match && field.optionKey) {
      match = field.options.find((opt: any) =>
        opt[field.optionKey] !== undefined && String(opt[field.optionKey]).trim().toLowerCase() === strVal
      );
    }

    if (match) {
      field.value = match.id;
    }
  }

  private isPlaceholderValue(value: any): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value !== 'string') return false;
    return ['', 'n/a', 'na', 'null', 'undefined', '---'].includes(value.trim().toLowerCase());
  }

  updateFormattedDate(item: any): void {
    let date: Date | null = null;

    if (item.value instanceof Date && !isNaN(item.value.getTime())) {
      date = item.value;
    } else if (typeof item.value === 'string') {
      const parsed = new Date(item.value);
      date = !isNaN(parsed.getTime()) ? parsed : null;
    }

    if (!date) {
      item.value = '';
      return;
    }

    const iso = date.toISOString().split('T')[0];
    item.value = item.monthYear ? iso.slice(0, 7) : iso;
  }

  openCalendar(fp: FlatpickrDirective, readonly = false): void {
    if (readonly) return;
    fp?.instance?.open();
  }

  validationMessage(item: any): string {
    return item.regexMessage || 'Invalid format';
  }

emitValue() {
  if (this.Singlefields) {
    if (this.singleFieldType === 'bitcase') {
      const decStr = this.singleFieldCalculatedTotal || '0';
      this.value.emit(decStr);
    } else {
      this.value.emit(this.inputValue);
    }
  }

  if (this.Multiplefields) {
    this.submitted = true;
    const invalidRequiredField = this.FormArray.find(field =>
      field.view !== false && field.required === true && field.readonly !== true && field.skipRequiredValidation !== true &&
      (field.value === undefined || field.value === null || String(field.value).trim() === '')
    );
    const invalidRegexField = this.FormArray.find(field => field.regex && field.value && field.isValid === false);
    if (invalidRequiredField) {
      this.toast.showToast(`${invalidRequiredField.placeholder || invalidRequiredField.id} is required.`, 'colored', 5000, 'danger');
      return;
    }
    if (invalidRegexField) return;
    const response: any = {};
    this.FormArray.forEach(field => {
      if (field.noPayload !== true) {
        const val = this.resolveFieldValue(field);
        if (val !== undefined && val !== null && val !== '') {
          response[field.id] = val;
        } else if (field.view !== false || field.required === true || field.display === false) {
          response[field.id] = field.value !== undefined ? field.value : val;
        }
      }
    });

    if (this.apiKey) {
      const cleanApiKey = this.apiKey.replace(/^\/+/, '');
      this.http.post(`${environment.apiURL}/${cleanApiKey}`, response).subscribe({
        next: (res: any) => {
          this.toast.showToast(res?.message || 'Submitted successfully', 'colored', 3000, 'success');
          this.emitformresponse(response);
          this.submitted = false;
          const modalEl = document.getElementById(this.modalId);
          if (modalEl) {
            const bootstrapModal = (window as any).bootstrap?.Modal;
            if (bootstrapModal) {
              const instance = bootstrapModal.getInstance(modalEl);
              instance?.hide();
            }
          }
        },
        error: (err: any) => {
          this.toast.showToast(err?.error?.message || 'Submission failed', 'colored', 5000, 'danger');
          this.submitted = false;
        }
      });
    } else {
      this.emitformresponse(response);
      this.submitted = false;
    }
  }
}

  private resolveFieldValue(field: any): any {
    if (field.type === 'bitcase') {
      const decStr = field.calculatedTotal ?? '0';
      try {
        const total = BigInt(decStr);
        return total <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(total) : total.toString(10);
      } catch {
        return Number(decStr) || 0;
      }
    }
    const isFilled = field.value !== undefined && field.value !== null 
    && field.value !== '' && field.value !== 'N/A';
    if (isFilled) {
      return field.value;
    }
    return null;
  }

  initializeBitFields(): void {
    (this.FormArray || []).forEach(field => {
      if (field?.type === 'bitcase') {
        this.initBitField(field);
      }
    });
  }

  initBitField(field: any): void {
    if (!field.conversionType) field.conversionType = 'decimal';
    if (!field.bitDirection) field.bitDirection = 'ltr';

    const hasMinBase = field.minBase !== undefined && field.minBase !== null && String(field.minBase).trim() !== '';
    const minBaseBig = hasMinBase ? this.parseBitValue(field.minBase, field.conversionType) : 0n;

    const hasMaxBase = field.maxBase !== undefined && field.maxBase !== null && String(field.maxBase).trim() !== '';
    const maxBaseBig = hasMaxBase ? this.parseBitValue(field.maxBase, field.conversionType) : 0n;

    let count = Number(field.bitCount);
    if (!count || count <= 0) {
      if (hasMinBase && hasMaxBase && maxBaseBig > minBaseBig) {
        const diff = maxBaseBig - minBaseBig;
        count = diff.toString(2).length;
      } else if (Array.isArray(field.bitLabels) && field.bitLabels.length > 0) {
        count = field.bitLabels.length;
      } else {
        count = 12;
      }
    }
    field.bitCount = count;

    let rawVal = field.value;
    if ((rawVal === undefined || rawVal === null || rawVal === '' || rawVal === 'N/A') && field.baseValue !== undefined && field.baseValue !== null && String(field.baseValue).trim() !== '') {
      rawVal = field.baseValue;
    }

    const num = this.parseBitValue(rawVal, field.conversionType);

    let effectiveOffset = 0n;
    if (hasMinBase) {
      effectiveOffset = num >= minBaseBig ? (num - minBaseBig) : 0n;
    } else {
      effectiveOffset = num;
    }

    let activeMask = 0n;
    const bits: any[] = [];
    for (let i = 0; i < count; i++) {
      const bitMask = 1n << BigInt(i);
      activeMask |= bitMask;
      const isChecked = Boolean((effectiveOffset >> BigInt(i)) & 1n);
      const customLabel = (Array.isArray(field.bitLabels) && field.bitLabels[i]) ? field.bitLabels[i] : '';
      const label = customLabel || bitMask.toString();
      bits.push({
        index: i,
        value: bitMask.toString(),
        label,
        checked: isChecked,
        isLsb: i === 0,
        isMsb: i === count - 1
      });
    }
    field.bits = bits;
    field.displayedBits = field.bitDirection === 'rtl' ? [...bits].reverse() : bits;

    if (hasMinBase) {
      field.hasMinBase = true;
      field.minBaseBig = minBaseBig;
      field.baseValue = minBaseBig;
      
      field.calculatedTotal = num.toString(10);
      field.value = this.formatBitValue(num, field.conversionType);
    } else {
      field.hasMinBase = false;
      field.baseValue = (num & ~activeMask);
      field.calculatedTotal = num.toString(10);
      field.value = this.formatBitValue(num, field.conversionType);
    }
  }

  initSingleFieldBits(): void {
    if (!this.conversionType) this.conversionType = 'decimal';
    const hasMinBase = this.minBase !== undefined && this.minBase !== null && String(this.minBase).trim() !== '';
    const minBaseBig = hasMinBase ? this.parseBitValue(this.minBase, this.conversionType) : 0n;
    const hasMaxBase = this.maxBase !== undefined && this.maxBase !== null && String(this.maxBase).trim() !== '';
    const maxBaseBig = hasMaxBase ? this.parseBitValue(this.maxBase, this.conversionType) : 0n;

    let count = Number(this.bitCount);
    if (!count || count <= 0) {
      if (hasMinBase && hasMaxBase && maxBaseBig > minBaseBig) {
        const diff = maxBaseBig - minBaseBig;
        count = diff.toString(2).length;
      } else if (Array.isArray(this.bitLabels) && this.bitLabels.length > 0) {
        count = this.bitLabels.length;
      } else {
        count = 12;
      }
    }
    this.bitCount = count;

    let rawVal = this.inputValue;
    if ((rawVal === undefined || rawVal === null || rawVal === '' || rawVal === 'N/A') && this.baseValue !== undefined && this.baseValue !== null && String(this.baseValue).trim() !== '') {
      rawVal = String(this.baseValue);
    }

    const num = this.parseBitValue(rawVal, this.conversionType);
    let effectiveOffset = 0n;
    if (hasMinBase) {
      effectiveOffset = num >= minBaseBig ? (num - minBaseBig) : 0n;
    } else {
      effectiveOffset = num;
    }

    let activeMask = 0n;
    const bits: any[] = [];
    for (let i = 0; i < count; i++) {
      const bitMask = 1n << BigInt(i);
      activeMask |= bitMask;
      const isChecked = Boolean((effectiveOffset >> BigInt(i)) & 1n);
      const customLabel = (Array.isArray(this.bitLabels) && this.bitLabels[i]) ? this.bitLabels[i] : '';
      const label = customLabel || bitMask.toString();
      bits.push({
        index: i,
        value: bitMask.toString(),
        label,
        checked: isChecked,
        isLsb: i === 0,
        isMsb: i === count - 1
      });
    }
    this.singleFieldBits = bits;
    this.displayedSingleFieldBits = this.bitDirection === 'rtl' ? [...bits].reverse() : bits;

    if (hasMinBase) {
      this.singleFieldHasMinBase = true;
      this.singleFieldMinBaseBig = minBaseBig;
      this.singleFieldBaseValue = minBaseBig;
      
      this.singleFieldCalculatedTotal = num.toString(10);
      this.inputValue = String(this.formatBitValue(num, this.conversionType));
    } else {
      this.singleFieldHasMinBase = false;
      this.singleFieldBaseValue = (num & ~activeMask);
      this.singleFieldCalculatedTotal = num.toString(10);
      this.inputValue = String(this.formatBitValue(num, this.conversionType));
    }
  }

  toggleBit(field: any, bit: any): void {
    if (field.readonly) return;
    bit.checked = !bit.checked;
    this.onBitToggle(field, bit);
  }

  toggleSingleBit(bit: any): void {
    bit.checked = !bit.checked;
    this.onSingleBitToggle(bit);
  }

  onBitToggle(field: any, bit: any): void {
    let total = 0n;
    if (field.hasMinBase) {
      let offset = 0n;
      let anyChecked = false;
      (field.bits || []).forEach((b: any) => {
        if (b.checked) {
          offset += (1n << BigInt(b.index));
          anyChecked = true;
        }
      });
      total = anyChecked ? ((field.minBaseBig !== undefined ? BigInt(field.minBaseBig) : 0n) + offset) : 0n;
    } else {
      total = field.baseValue !== undefined ? BigInt(field.baseValue) : 0n;
      (field.bits || []).forEach((b: any) => {
        if (b.checked) {
          total += (1n << BigInt(b.index));
        }
      });
    }
    field.calculatedTotal = total.toString(10);
    field.value = this.formatBitValue(total, field.conversionType);
    this.validateInput(field);
  }

  onSingleBitToggle(bit: any): void {
    let total = 0n;
    if (this.singleFieldHasMinBase) {
      let offset = 0n;
      let anyChecked = false;
      (this.singleFieldBits || []).forEach((b: any) => {
        if (b.checked) {
          offset += (1n << BigInt(b.index));
          anyChecked = true;
        }
      });
      total = anyChecked ? (this.singleFieldMinBaseBig + offset) : 0n;
    } else {
      total = this.singleFieldBaseValue !== undefined ? BigInt(this.singleFieldBaseValue) : 0n;
      (this.singleFieldBits || []).forEach((b: any) => {
        if (b.checked) {
          total += (1n << BigInt(b.index));
        }
      });
    }
    this.singleFieldCalculatedTotal = total.toString(10);
    this.inputValue = String(this.formatBitValue(total, this.conversionType));
  }

  selectAllBits(field: any): void {
    if (field.readonly) return;
    (field.bits || []).forEach((b: any) => (b.checked = true));
    this.onBitToggle(field, null);
  }

  clearAllBits(field: any): void {
    if (field.readonly) return;
    (field.bits || []).forEach((b: any) => (b.checked = false));
    this.onBitToggle(field, null);
  }

  selectAllSingleBits(): void {
    this.singleFieldBits.forEach((b: any) => (b.checked = true));
    this.onSingleBitToggle(null);
  }

  clearAllSingleBits(): void {
    this.singleFieldBits.forEach((b: any) => (b.checked = false));
    this.onSingleBitToggle(null);
  }

  evaluateAllFieldConditions(): void {
    if (!Array.isArray(this.FormArray) || !this.FormArray.length) return;

    const currentContext: any = { ...(this.rowData || {}) };
    this.FormArray.forEach(f => {
      if (f && f.id) {
        currentContext[f.id] = f.value;
      }
    });

    this.FormArray.forEach(f => {
      if (f && f.conditionEnabled && (f.conditionKey || (Array.isArray(f.conditions) && f.conditions.length))) {
        const passed = this.evaluateFieldConditionRules(f, currentContext);
        if (f.type === 'bitcase') {
          // For bitcase/checkbox: the whole input won't hide. Only the checkbox will hide.
          f.view = f.view !== false;
          f.hideCheckboxes = !passed;
        } else {
          f.view = passed;
          f.skipRequiredValidation = !passed;
        }
      }
    });
  }

  private evaluateFieldConditionRules(field: any, data: any): boolean {
    if (!field.conditionEnabled) return true;

    let rules: any[] = [];
    if (Array.isArray(field.conditions) && field.conditions.length > 0) {
      rules = field.conditions.filter((c: any) => c && c.key);
    }
    if (!rules.length && field.conditionKey) {
      rules = [{
        key: field.conditionKey,
        operator: field.conditionOperator || '==',
        value: field.conditionValue !== undefined ? String(field.conditionValue) : '',
        logic: 'AND'
      }];
    }

    if (!rules.length) return true;

    let overallResult = this.evaluateSingleConditionRule(data[rules[0].key], rules[0].operator, rules[0].value);

    for (let i = 1; i < rules.length; i++) {
      const rule = rules[i];
      const ruleResult = this.evaluateSingleConditionRule(data[rule.key], rule.operator, rule.value);
      const logic = String(rule.logic || field.conditionLogic || 'AND').toUpperCase().trim();

      if (logic === 'OR' || logic === '||') {
        overallResult = overallResult || ruleResult;
      } else {
        overallResult = overallResult && ruleResult;
      }
    }

    return overallResult;
  }

  private evaluateSingleConditionRule(rowVal: any, op: string, targetVal: any): boolean {
    const rowStr = rowVal !== undefined && rowVal !== null ? String(rowVal).trim() : '';
    const targetStr = targetVal !== undefined && targetVal !== null ? String(targetVal).trim() : '';
    const targetItems = targetStr ? targetStr.split(',').map(s => s.trim().toLowerCase()).filter(Boolean) : [];
    const rowLower = rowStr.toLowerCase();

    switch (op) {
      case '!=':
        if (targetItems.length > 1) {
          return !targetItems.includes(rowLower);
        }
        return rowLower !== targetStr.toLowerCase();
      case 'truthy':
        return rowVal !== undefined && rowVal !== null && rowStr !== '' && rowStr !== '0' && rowLower !== 'false' && rowLower !== 'n';
      case 'falsy':
        return rowVal === undefined || rowVal === null || rowStr === '' || rowStr === '0' || rowLower === 'false' || rowLower === 'n';
      case 'in':
        return targetItems.includes(rowLower);
      case 'notIn':
        return !targetItems.includes(rowLower);
      case 'contains':
      case 'has':
        if (!targetItems.length) return true;
        return targetItems.some(item => rowLower.includes(item));
      case '==':
      default:
        if (targetItems.length > 1) {
          return targetItems.includes(rowLower);
        }
        return rowLower === targetStr.toLowerCase();
    }
  }

  getConversionBadge(convType?: string): string {
    const t = String(convType || 'decimal').toLowerCase();
    if (t === 'hexadecimal' || t === '16' || t === 'hex') return 'Hex (16)';
    if (t === 'octal' || t === '8') return 'Oct (8)';
    return 'Dec (2)';
  }

  getConversionLabel(convType?: string): string {
    const t = String(convType || 'decimal').toLowerCase();
    if (t === 'hexadecimal' || t === '16' || t === 'hex') return 'Hexadecimal';
    if (t === 'octal' || t === '8') return 'Octal';
    return 'Decimal';
  }

  formatBitValue(total: bigint, convType?: string): any {
    const t = String(convType || 'decimal').toLowerCase();
    if (t === 'hexadecimal' || t === '16' || t === 'hex') {
      return total.toString(16).toUpperCase();
    }
    if (t === 'octal' || t === '8') {
      return total.toString(8);
    }
    if (total <= BigInt(Number.MAX_SAFE_INTEGER)) {
      return Number(total);
    }
    return total.toString(10);
  }

  parseBitValue(val: any, convType?: string): bigint {
    if (val === null || val === undefined || val === '' || val === 'N/A') return 0n;
    const str = String(val).trim();
    const t = String(convType || 'decimal').toLowerCase();
    try {
      if (t === 'hexadecimal' || t === '16' || t === 'hex') {
        const clean = str.replace(/^0x/i, '').trim();
        return clean ? BigInt('0x' + clean) : 0n;
      }
      if (t === 'octal' || t === '8') {
        const clean = str.replace(/^0o/i, '').trim();
        return clean ? BigInt('0o' + clean) : 0n;
      }
      return BigInt(str);
    } catch {
      return 0n;
    }
  }
  emitformresponse(data: any) {
    this.fomrresponse.emit(data);
  }
}
